import { zodResolver } from "@hookform/resolvers/zod";
import { UpdateIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from "zod";
import '../App.css';
import PasswordValidation from '../components/passwordValidation';
import { Button } from '../components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import { Checkbox } from '../components/ui/checkbox';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';

const signupSchema = z.object({
    first_name: z.string({
        required_error: "Förnamn krävs.",
    }).min(1, {
        message: "Förnamn måste vara minst 1 tecken."
    }).max(255, {
        message: "Förnamn måste vara max 255 tecken."
    }).trim(),
    last_name: z.string({
        required_error: "Efternamn krävs.",
    }).min(1, {
        message: "Efternamn måste vara minst 1 tecken."
    }).max(255, {
        message: "Efternamn måste vara max 255 tecken."
    }).trim(),
    phone: z.string({
        required_error: "Telefonnummer krävs.",
    })
        .regex(/^\+?\d{8,30}$/, {
            message: "Ogiltigt telefonnummer."
        }),
    birth_year: z.string({
        required_error: "Födelseår krävs.",
    })
        .regex(/^\d{4}$/, { message: "Födelseår måste vara ett giltigt årtal." })
        .refine(year => {
            const numericYear = parseInt(year, 10);
            const currentYear = new Date().getFullYear();
            return numericYear >= 1900 && numericYear <= currentYear;
        }, {
            message: "Födelseår måste vara mellan 1900 och innevarande år."
        }),
    address: z.string({
        required_error: "Adress krävs.",
    }).min(5, {
        message: "Adress måste vara minst 5 tecken."
    }).max(255, {
        message: "Adress måste vara max 255 tecken."
    }).trim(),
    email: z.string({
        required_error: "E-post krävs."
    }).email({
        message: "Ogiltig e-postadress."
    }).trim(),
    password: z.string({
        required_error: "Lösenord krävs."
    })
        .min(8, "Lösenordet måste vara mellan 8 och 20 tecken långt.")
        .max(20, "Lösenordet måste vara mellan 8 och 20 tecken långt.")
        .regex(/(?=.*[0-9])/, "Lösenordet måste innehålla minst en siffra (0-9).")
        .regex(/(?=.*[a-zåäö])/, "Lösenordet måste innehålla minst en liten bokstav (a-ö).")
        .regex(/(?=.*[A-ZÅÄÖ])/, "Lösenordet måste innehålla minst en stor bokstav (A-Ö).")
        .regex(/(?=.*[@!.#$%^&-+=()])/, "Lösenordet måste innehålla minst ett specialtecken (@!.#$%^&-+=()).")
        .refine(password => !password.includes(" "), {
            message: "Lösenord får inte innehålla mellanslag."
        }),
    password_confirmation: z.string({
        required_error: "Bekräfta lösenord krävs."
    }),
    parent_email: z.string()
        .email({ message: "Ogiltig e-postadress." })
        .optional()
        .or(z.literal('')),
    acceptance: z.literal(true, {
        errorMap: () => ({ message: "Du måste acceptera integritetspolicyn." })
    })
}).refine(data => data.password === data.password_confirmation, {
    message: "Lösenorden matchar inte.",
    path: ["password_confirmation"],
}).refine(data => {
    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(data.birth_year, 10);
    return age >= 18 || (data.parent_email && data.parent_email.trim().length > 0);
}, {
    message: "Förälders e-post krävs om du är under 18 år.",
    path: ["parent_email"],
});

function Signup() {
    const { auth, isLoggedIn } = useContext(AuthContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showParentEmail, setShowParentEmail] = useState(false);


    const form = useForm({
        resolver: zodResolver(signupSchema),
        defaultValues: {
            first_name: "",
            last_name: "",
            birth_year: "",
            address: "",
            phone: "",
            email: "",
            password: "",
            password_confirmation: "",
            acceptance: false,
        }
    });

    const birth_year = form.watch('birth_year');

    useEffect(() => {
        if (isLoggedIn()) {
            navigate("/clubs", {
                state: {
                    toast: {
                        variant: 'destructive',
                        title: 'Något gick fel.',
                        description: "Du kan inte registrera en användare om du redan är inloggad. Logga först ut för att registrera användaren.",
                    },
                },
            });
        }
    }, [auth, isLoggedIn, navigate]);

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const birth_year = form.getValues('birth_year')
        const age = currentYear - birth_year;
        if (birth_year && age < 18) {
            setShowParentEmail(true);
        } else {
            setShowParentEmail(false);
            form.setValue('parent_email', '');
        }
    }, [birth_year, form]);

    async function onSubmit(values) {
        if (isLoading) return;

        setIsLoading(true);

        let urlParams = new URLSearchParams(window.location.search);
        let secretKey = urlParams.get('secret');

        if (!secretKey) {
            console.error("Secret key is missing from the URL");
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: 'Hemlig nyckel saknas, kontrollera länken du fick.',
            });
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.post(`https://skhrf-api.cmdigital.se/v1/auth/signup`, {
                ...values,
                secretkey: secretKey
            });
            if (response.status === 201) {
                navigate('/login');
                toast({
                    variant: 'success',
                    title: 'Registrering lyckad!',
                    description: 'Använd de inloggningsuppgifter du angav precis för att logga in.'
                })
            }
        } catch (error) {
            console.error("Error Response:", error.response?.data || error.message);

            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error || "Ett okänt fel uppstod.",
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className='flex w-full py-12 items-center justify-center'>
            <Card className='w-3/4 lg:w-7/12 shadow-md'>
                <CardHeader>
                    <CardTitle>Skapa konto på STAMP</CardTitle>
                    {/* <CardDescription>Ange nedan information för att skapa ditt konto på STAMP</CardDescription> */}
                </CardHeader>
                <CardContent className='grid gap-4'>
                    <Form {...form} >
                        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
                            <div className='grid md:grid-cols-2 gap-3 items-start'>
                                <FormField
                                    control={form.control}
                                    name="first_name"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='first_name'>Förnamn</FormLabel>
                                            <FormControl>
                                                <Input id='first_name' type='text' placeholder='Namn' autoComplete='given-name' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="last_name"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='last_name'>Efternamn</FormLabel>
                                            <FormControl>
                                                <Input id='last_name' type='text' placeholder='Efternamn' autoComplete='family-name' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='grid md:grid-cols-2 gap-3 items-start'>
                                <FormField
                                    control={form.control}
                                    name="phone"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='phone'>Telefonnummer</FormLabel>
                                            <FormControl>
                                                <Input id='phone' type='phone' placeholder='070 123 45 67' autoComplete='tel' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="birth_year"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='birth_year'>Födelseår</FormLabel>
                                            <FormControl>
                                                <Input id='birth_year' type='number' placeholder='1900' autoComplete='bday-year' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div>
                                <FormField
                                    control={form.control}
                                    name="address"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='address'>Adress</FormLabel>
                                            <FormControl>
                                                <Input id='address' type='text' placeholder='Storgatan 1, 12345, Stora staden' autoComplete='street-address' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            {showParentEmail && (
                                <div>
                                    <FormField
                                        control={form.control}
                                        name="parent_email"
                                        render={({ field }) => (
                                            <FormItem className='space-y-0 grid gap-2'>
                                                <FormLabel htmlFor='parent_email'>E-postadress till förälder</FormLabel>
                                                <FormControl>
                                                    <Input id='parent_email' type='email' placeholder='kontakt@cmdigital.se' autoComplete='off' {...field} />
                                                </FormControl>
                                                <FormDescription>
                                                    Du behöver ha en förälders godkännande för att skapa ett konto om du är under 18 år. All e-post som du kommer ta emot från systemet kommer även skickas till din förälder.
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            )}
                            <Separator />
                            <div>
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='email'>E-post</FormLabel>
                                            <FormControl>
                                                <Input id='email' placeholder='hej@cmdigital.se' autoComplete='email' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='grid md:grid-cols-2 gap-3 items-start'>
                                <FormField
                                    control={form.control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='password'>Lösenord</FormLabel>
                                            <FormControl>
                                                <Input id='password' type='password' autoComplete='new-password' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="password_confirmation"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel htmlFor='password_confirmation'>Bekräfta lösenord</FormLabel>
                                            <FormControl>
                                                <Input id='password_confirmation' type='password' autoComplete='new-password' {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='grid gap-2'>
                                <FormField
                                    control={form.control}
                                    name="acceptance"
                                    render={({ field }) => (
                                        <FormItem className='flex flex-row items-start gap-2 space-y-0'>
                                            <FormControl>
                                                <Checkbox id='acceptance' checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                            <div className='space-y-1 leading-none'>
                                                <FormLabel htmlFor='acceptance'>Acceptera integritetspolicy</FormLabel>
                                                <FormDescription>
                                                    Du behöver acceptera vår integritetspolicy för att skapa ett konto i systemet. Var god läs vår integritetspolicy <a className='text-skhrf-blue' href='https://skhrf.se/integritetspolicy/' target='_blank' rel="noopener noreferrer">här</a>.
                                                </FormDescription>
                                            </div>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={isLoading || !form.watch('acceptance')}>
                                {isLoading ? <UpdateIcon className='loader-spinner' /> : <>Skapa konto</>}
                            </Button>
                            <div>
                                <PasswordValidation password={form.watch('password')} />
                            </div>
                        </form>
                    </Form>

                </CardContent>
            </Card>
        </div>
    );
}

export default Signup;
